import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@h2/ui';
import { Input } from '@h2/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@h2/ui';
import { Badge } from '@h2/ui';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Star,
  Copy,
  MoreHorizontal,
  Grid,
  List,
  Clock,
  BarChart3
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@h2/ui';
import { useWorkPresetStore, WorkPreset } from '../stores/useWorkPresetStore';
import { PresetFormModal } from '../components/preset/PresetFormModal';
import { PresetDeleteModal } from '../components/preset/PresetDeleteModal';

export const WorkPresetManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    filteredPresets,
    isLoading,
    searchQuery,
    selectedPreset,
    loadPresets,
    setSearchQuery,
    selectPreset,
    clearSelection,
    toggleFavorite
  } = useWorkPresetStore();

  const [editingPreset, setEditingPreset] = useState<WorkPreset | null>(null);
  const [deletingPreset, setDeletingPreset] = useState<WorkPreset | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  useEffect(() => {
    console.log('WorkPresetManagementPage: loadPresets 호출');
    loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    console.log('WorkPresetManagementPage: filteredPresets 업데이트됨:', filteredPresets.length, '개');
  }, [filteredPresets]);

  const handleEdit = (preset: WorkPreset) => {
    setEditingPreset(preset);
  };

  const handleDelete = (preset: WorkPreset) => {
    setDeletingPreset(preset);
  };

  const handleDuplicate = async (preset: WorkPreset) => {
    // 프리셋 복제 로직
    const duplicatedPreset = {
      ...preset,
      name: `${preset.name} (복사본)`,
      id: `${preset.id}_copy_${Date.now()}`
    };
    // TODO: 실제 복제 API 호출
    console.log('Duplicate preset:', duplicatedPreset);
  };

  const handleToggleFavorite = async (preset: WorkPreset) => {
    await toggleFavorite(preset.id);
  };

  const handleFormSave = () => {
    setEditingPreset(null);
  };

  const handleFormCancel = () => {
    setEditingPreset(null);
  };

  const handleDeleteConfirm = async () => {
    if (deletingPreset) {
      // TODO: 실제 삭제 API 호출
      console.log('Delete preset:', deletingPreset.id);
      setDeletingPreset(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">작업프리셋 라이브러리</h1>
          <p className="text-muted-foreground">
            재사용 가능한 작업 설정 템플릿을 관리합니다
          </p>
        </div>
        <Button onClick={() => navigate('/work/presets/create')}>
          <Plus className="h-4 w-4 mr-2" />
          새 프리셋
        </Button>
      </div>

      {/* 검색 및 보기 모드 */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="프리셋 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* 보기 모드 */}
        <div className="flex border rounded-lg">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 프리셋 목록 */}
      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">프리셋을 불러오는 중...</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          : "space-y-3"
        }>
          {filteredPresets.map((preset) => (
            <PresetCard
              key={preset.id}
              preset={preset}
              viewMode={viewMode}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {filteredPresets.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-semibold mb-2">프리셋이 없습니다</h3>
          <p className="text-muted-foreground mb-4">
            첫 번째 작업 프리셋을 생성해 보세요
          </p>
          <Button onClick={() => navigate('/work/presets/create')}>
            <Plus className="h-4 w-4 mr-2" />
            프리셋 생성
          </Button>
        </div>
      )}

      {/* 편집 모달 */}
      {editingPreset && (
        <PresetFormModal
          preset={editingPreset}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}

      {deletingPreset && (
        <PresetDeleteModal
          preset={deletingPreset}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingPreset(null)}
        />
      )}
    </div>
  );
};

interface PresetCardProps {
  preset: WorkPreset;
  viewMode: 'grid' | 'list';
  onEdit: (preset: WorkPreset) => void;
  onDelete: (preset: WorkPreset) => void;
  onDuplicate: (preset: WorkPreset) => void;
  onToggleFavorite: (preset: WorkPreset) => void;
}

const PresetCard: React.FC<PresetCardProps> = ({
  preset,
  viewMode,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleFavorite
}) => {
  const isFavorite = preset.userInteraction?.isFavorite || false;
  const usageCount = preset.metadata?.statistics?.totalUsages || 0;
  const successRate = preset.metadata?.statistics?.successRate || 0;
  const estimatedDuration = preset.metadata?.performance?.estimatedDuration || { value: 1, unit: 'day' as const };
  
  // 언어 정보 생성
  const languageSettings = preset.configData?.languageSettingsComponent;
  const sourceLanguage = languageSettings?.sourceLanguage || '';
  const targetLanguages = languageSettings?.targetLanguages || [];
  
  const languageCodeToName = (code: string) => {
    const languageMap: { [key: string]: string } = {
      'ko': '한국어',
      'en': '영어', 
      'ja': '일본어',
      'zh': '중국어',
      'es': '스페인어',
      'hi': '힌디어'
    };
    return languageMap[code] || code;
  };
  
  const getLanguageDisplay = () => {
    if (!sourceLanguage && targetLanguages.length === 0) {
      return '언어 미설정';
    }
    
    if (!sourceLanguage) {
      return `${targetLanguages.map(languageCodeToName).join(', ')}`;
    }
    
    if (targetLanguages.length === 0) {
      return `${languageCodeToName(sourceLanguage)} (전사전용)`;
    }
    
    return `${languageCodeToName(sourceLanguage)} → ${targetLanguages.map(languageCodeToName).join(', ')}`;
  };

  if (viewMode === 'list') {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{preset.name}</h3>
                  {isFavorite && <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {preset.description}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {getLanguageDisplay()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <BarChart3 className="h-4 w-4" />
                <span>{usageCount}회 사용</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{estimatedDuration.value}{estimatedDuration.unit === 'hour' ? '시간' : '일'}</span>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => onEdit(preset)}>
                  <Edit className="h-4 w-4 mr-2" />
                  편집
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicate(preset)}>
                  <Copy className="h-4 w-4 mr-2" />
                  복제
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleFavorite(preset)}>
                  <Star className="h-4 w-4 mr-2" />
                  {isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onDelete(preset)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  삭제
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{preset.name}</CardTitle>
            <div className="flex items-center gap-2 mt-1">
              {isFavorite && <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />}
              {preset.tags.slice(0, 2).map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onEdit(preset)}>
                <Edit className="h-4 w-4 mr-2" />
                편집
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(preset)}>
                <Copy className="h-4 w-4 mr-2" />
                복제
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleFavorite(preset)}>
                <Star className="h-4 w-4 mr-2" />
                {isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onDelete(preset)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                삭제
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <CardDescription className="line-clamp-2">
          {preset.description}
        </CardDescription>
        
        <div className="text-sm text-blue-600 font-medium">
          {getLanguageDisplay()}
        </div>
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <BarChart3 className="h-3 w-3" />
            <span>{usageCount}회</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{estimatedDuration.value}{estimatedDuration.unit === 'hour' ? 'h' : 'd'}</span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button size="sm" className="flex-1" onClick={() => onEdit(preset)}>
            적용
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(preset)}>
            편집
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
